import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Plus,
  X,
  ChevronDown,
  Warning,
  LogIn,
  People,
} from '@/components/ui/icons';
import { UserAvatar } from '@/components/chat/UserAvatar';
import { AccountSwitcher } from '@/components/auth/AccountSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useHomepage } from '@/contexts/homepage-context';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNotifications } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { TabShape } from '@/components/ui/TabShape';
import { formatDistanceToNow } from 'date-fns';
import type { Tab } from '@/types/tabTypes';
import { CartMenuButton } from './appMenuBar/CartMenuButton';
import { FixedTab } from './appMenuBar/FixedTab';
import { useTabDrag } from './appMenuBar/useTabDrag';
import {
  SIDEBAR_OPEN_PX,
  SIDEBAR_EDGE_PX,
  TAB_W,
  TAB_H,
  EAR,
  TOP_RADIUS,
  BOTTOM_RAMP,
  OVERLAP,
  STROKE_W,
  STRIP_LEFT,
  MERGE_OFFSET,
  TAB_RAMP_DY,
  BAR_H,
  NEWTAB_W,
  NEWTAB_H,
  NEWTAB_R,
  FILL_ACTIVE,
  FILL_INACTIVE,
  INACTIVE_TAB_OPACITY,
  STROKE_TAB,
  SEAM,
  NEWTAB_SKEW,
  NEWTAB_MARGIN_LEFT,
} from './appMenuBar/constants';

interface AppMenuBarProps {
  onToggleRightSidebar: () => void;
  isSidebarOpen?: boolean;
  showTabs?: boolean;
  tabs?: Tab[];
  activeTabId?: string | null;
  onTabClick?: (id: string) => void;
  onTabClose?: (id: string) => void;
  onNewTab?: () => void;
  onReorderTabs?: (fromId: string, toId: string) => void;
  onDocumentsClick?: () => void;
  isDocumentsActive?: boolean;
  onMarketplaceClick?: () => void;
  isMarketplaceActive?: boolean;
}

export function AppMenuBar({
  onToggleRightSidebar,
  isSidebarOpen = false,
  showTabs = false,
  tabs = [],
  activeTabId,
  onTabClick,
  onTabClose,
  onNewTab,
  onReorderTabs,
  onDocumentsClick,
  isDocumentsActive = false,
  onMarketplaceClick,
  isMarketplaceActive = false,
}: AppMenuBarProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Guests get literal Log In / Sign Up buttons instead of an account control.
  const isAnonymous = !user || user.is_anonymous;
  const { featureTreeOpen } = useHomepage();
  const isMobile = useIsMobile();
  const { notifications, unreadCount, markRead, markAllRead } =
    useNotifications();
  const location = useLocation();
  const isHomeLike =
    location.pathname === '/' ||
    location.pathname.startsWith('/editor/') ||
    location.pathname.startsWith('/files') ||
    location.pathname.startsWith('/market');

  // Documents / Marketplace render a fixed blank rail (BlankShellSidebar) at
  // the open width regardless of the Feature Tree toggle, so the tab strip
  // must pad by the open width there too or it slides under the rail.
  const hasFixedRail =
    location.pathname.startsWith('/files') ||
    location.pathname.startsWith('/market');
  const leftPad =
    isHomeLike && !isMobile
      ? featureTreeOpen || hasFixedRail
        ? SIDEBAR_OPEN_PX
        : SIDEBAR_EDGE_PX
      : 0;

  const atLimit = tabs.length >= 5;

  const { tabElemsRef, dragState, handleMouseDown, getTabStyle } = useTabDrag({
    tabs,
    onTabClick,
    onReorderTabs,
  });
  const [notifOpen, setNotifOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);

  const activeIndex = tabs.findIndex((t) => t.id === activeTabId);
  // Mountain stacking: active tab is the peak; tabs nest back the further they
  // are from it; the dragged tab floats above everything.
  const tabZ = (index: number, isDragging: boolean) =>
    isDragging
      ? 200
      : activeIndex >= 0
        ? 100 - Math.abs(index - activeIndex)
        : index;

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const menuItemClass =
    'rounded px-3 py-0.5 text-xs text-telekinesis-text-secondary transition-colors hover:bg-telekinesis-neutral-700 hover:text-telekinesis-text-primary';
  const dropItemClass =
    'cursor-pointer text-xs text-telekinesis-text-primary hover:bg-telekinesis-neutral-700 focus:bg-telekinesis-neutral-700';
  const dropContentClass =
    'border-telekinesis-neutral-700 bg-[#111518] text-telekinesis-text-primary';

  return (
    <div
      className="fixed left-0 right-0 top-0 z-50 hidden bg-black lg:flex"
      style={{ height: BAR_H, userSelect: dragState ? 'none' : undefined }}
    >
      {/* THE SEAM — a single, full-width 1px line at the bar's bottom edge, the
          one continuous outline the active tab merges into (harness: the feature
          toolbar's borderTop). Owning it here — full width, always present, in
          the SAME coordinate space as the tabs — kills the three production bugs:
          no two-piece strip↔toolbar-border boundary to nick (missing border), no
          second border drawn by the toolbar (double border), and perfect
          alignment with every tab bottom (no jink). It sits at z 0, BELOW the
          tabs (z >= ~96 via tabZ), so the active tab's #16191b fill covers it and
          its open-bottom ramps rise out of it, exactly like the harness; inactive
          tabs sit on it; the gaps show it raw. TopToolbar draws bg #16191b (no
          border) directly beneath, so the active fill and the toolbar surface are
          the same color and read as one shape. bottom:-1 places it at y 39..40,
          where the toolbar surface begins. */}
      {showTabs && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0"
          style={{ bottom: -1, height: 1, backgroundColor: SEAM, zIndex: 0 }}
        />
      )}

      {/* Brand — absolute so it doesn't shift menu items; collapses when feature tree is hidden */}
      {featureTreeOpen && (
        <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center gap-1.5 text-xs font-medium text-telekinesis-text-secondary">
          <img src="/favicon-64x64.png" alt="" className="h-4 w-4" />
          Telekinesis v0.1
        </span>
      )}

      {/* Menu items — vertically centered in the black bar, offset past the sidebar */}
      <div
        className="flex shrink-0 items-center gap-0.5 transition-all duration-300"
        style={{ paddingLeft: leftPad }}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(menuItemClass, 'flex items-center gap-1.5 py-1')}
            >
              File
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={dropContentClass} align="start">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <DropdownMenuItem
                    className={cn(dropItemClass, atLimit && 'opacity-40')}
                    disabled={atLimit}
                    onClick={atLimit ? undefined : onNewTab}
                  >
                    New File
                  </DropdownMenuItem>
                </div>
              </TooltipTrigger>
              {atLimit && (
                <TooltipContent>Sorry, Davinci. 5 Tab Maximum.</TooltipContent>
              )}
            </Tooltip>
            <DropdownMenuItem
              className={dropItemClass}
              onClick={() => navigate('/files')}
            >
              Open Files
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Documents / Marketplace — active = merged tab; hover = grey ghost tab. */}
        <FixedTab
          label="Documents"
          width={120}
          active={isDocumentsActive}
          onClick={onDocumentsClick}
          pageFill="#121516" /* telekinesis-background-1 — FilesView bg */
        />
        <FixedTab
          label="Marketplace"
          width={130}
          active={isMarketplaceActive}
          onClick={onMarketplaceClick}
          pageFill="#0C0F11" /* telekinesis-bg-dark — MarketplaceView bg */
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(menuItemClass, 'flex items-center gap-1.5 py-1')}
            >
              Settings
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={dropContentClass} align="start">
            <DropdownMenuItem
              className={dropItemClass}
              onClick={() => navigate('/settings')}
            >
              Preferences
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-telekinesis-neutral-700" />
            <DropdownMenuItem
              className={dropItemClass}
              onClick={() => navigate('/subscription')}
            >
              Subscription
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tab fan — trapezoids, right-laps-left, mountain-stacked, active merges */}
      {showTabs && (
        <div
          className="flex flex-1 items-end overflow-visible"
          style={{ marginLeft: STRIP_LEFT }}
        >
          {tabs.map((tab, index) => {
            const isActive =
              tab.id === activeTabId &&
              !isDocumentsActive &&
              !isMarketplaceActive;
            const isDragging =
              dragState?.tabId === tab.id && Math.abs(dragState.deltaX) > 3;
            // Exactly one green: hovered tab if any, else the active tab.
            const greenTitle =
              hoveredTabId !== null ? hoveredTabId === tab.id : isActive;
            const merged = isActive;

            return (
              <div
                key={tab.id}
                ref={(el) => {
                  if (el) tabElemsRef.current.set(tab.id, el);
                  else tabElemsRef.current.delete(tab.id);
                }}
                style={{
                  width: TAB_W,
                  height: TAB_H,
                  marginLeft: index === 0 ? 0 : -OVERLAP,
                  // FROZEN vertical position: every tab sits at the active drop
                  // (MERGE_OFFSET) whether active or not. Tabs no longer move
                  // up/down between states, so a tab calibrated to the seam
                  // (via TAB_RAMP_DY) stays aligned when it goes inactive.
                  marginBottom: MERGE_OFFSET,
                  zIndex: tabZ(index, isDragging),
                  ...getTabStyle(tab, index),
                }}
                className="group pointer-events-none relative shrink-0"
              >
                <TabShape
                  variant="trapezoid"
                  width={TAB_W}
                  height={TAB_H}
                  ear={EAR}
                  radius={TOP_RADIUS}
                  bottomRadius={BOTTOM_RAMP}
                  strokeWidth={STROKE_W}
                  fill={isActive ? FILL_ACTIVE : FILL_INACTIVE}
                  fillOpacity={isActive ? 1 : INACTIVE_TAB_OPACITY}
                  occluder={isActive ? undefined : '#000'}
                  stroke={merged ? SEAM : STROKE_TAB}
                  openBottom={merged}
                  rampDy={TAB_RAMP_DY}
                  className={cn(
                    'transition-colors',
                    !isActive && 'group-hover:[&>path]:fill-[#0C0C0C]',
                  )}
                  onMouseDown={(e) => handleMouseDown(e, tab, index)}
                />
                <div
                  className={cn(
                    'pointer-events-auto absolute inset-0 flex cursor-pointer items-center gap-1.5 text-xs',
                    isActive
                      ? 'text-telekinesis-text-primary'
                      : 'text-telekinesis-text-secondary',
                  )}
                  style={{ paddingLeft: EAR + 4, paddingRight: OVERLAP + 8 }}
                  onMouseEnter={() => setHoveredTabId(tab.id)}
                  onMouseLeave={() => setHoveredTabId(null)}
                  onMouseDown={(e) => handleMouseDown(e, tab, index)}
                >
                  <span
                    className={cn(
                      'flex-1 truncate transition-colors',
                      greenTitle
                        ? 'text-[#107d40]'
                        : 'text-telekinesis-text-secondary',
                    )}
                  >
                    {tab.title}
                  </span>
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTabClose?.(tab.id);
                    }}
                    className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded opacity-100 transition-all hover:text-[#FF4D4D] hover:[filter:drop-shadow(0_0_3px_#FF4D4D)]"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* New Tab — translucent left-leaning rhombus, sides parallel to slant */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'group relative shrink-0 self-end',
                  atLimit ? 'opacity-40' : 'cursor-pointer',
                )}
                style={{
                  width: NEWTAB_W,
                  height: NEWTAB_H,
                  marginLeft: NEWTAB_MARGIN_LEFT,
                  marginBottom: (TAB_H - NEWTAB_H) / 2,
                  zIndex: 150,
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={atLimit ? undefined : onNewTab}
              >
                <TabShape
                  variant="rhombus-left"
                  width={NEWTAB_W}
                  height={NEWTAB_H}
                  ear={NEWTAB_SKEW}
                  radius={NEWTAB_R}
                  fill={FILL_INACTIVE}
                  fillOpacity={INACTIVE_TAB_OPACITY}
                  occluder="#000"
                  strokeWidth={STROKE_W}
                  stroke={STROKE_TAB}
                  className={cn(
                    'transition-all',
                    !atLimit && [
                      'group-hover:[&>path:last-child]:[stroke:rgba(255,186,40,0.55)]',
                      'group-hover:[&>path:last-child]:[filter:drop-shadow(0_0_4px_rgba(255,186,40,0.55))]',
                    ],
                  )}
                />
                <div
                  className={cn(
                    'pointer-events-none absolute inset-0 flex items-center justify-center text-telekinesis-text-secondary transition-all',
                    !atLimit && [
                      'group-hover:[color:rgba(255,186,40,0.55)]',
                      'group-hover:[filter:drop-shadow(0_0_4px_rgba(255,186,40,0.55))]',
                    ],
                  )}
                >
                  <Plus className="h-3.5 w-3.5" />
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start">
              {atLimit ? 'Sorry, Davinci. 5 Tab Maximum.' : 'New Tab'}
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Online / Offline indicator */}
      <div className="flex items-center px-3">
        {isOnline ? (
          <span className="flex items-center gap-1.5 font-mono text-[13px] text-[#107d40]">
            <span className="h-1 w-1 shrink-0 rounded-full bg-[#107d40]" />
            Online
          </span>
        ) : (
          <span className="flex items-center gap-1.5 font-mono text-[13px] text-red-500">
            <Warning className="h-3.5 w-3.5 shrink-0" />
            Offline
          </span>
        )}
      </div>

      {/* Marketplace cart — renders itself only on /market*. */}
      <CartMenuButton />

      {/* Notification bell */}
      <DropdownMenu
        open={notifOpen}
        onOpenChange={(o) => {
          setNotifOpen(o);
          if (o && unreadCount > 0) markAllRead();
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'relative flex h-full items-center justify-center border-l border-telekinesis-neutral-800 px-3 transition-colors',
              notifOpen
                ? 'bg-telekinesis-bg-dark text-telekinesis-text-primary'
                : 'text-telekinesis-text-secondary hover:bg-telekinesis-neutral-900 hover:text-telekinesis-text-primary',
            )}
          >
            <Bell className="h-3.5 w-3.5" />
            {unreadCount > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-[#FFBA28] px-0.5 font-mono text-[9px] font-bold leading-none text-black">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={cn(dropContentClass, 'w-72')}
        >
          <div className="flex items-center justify-between border-b border-telekinesis-neutral-700 px-3 py-2">
            <span className="text-xs font-medium text-telekinesis-text-primary">
              Notifications
            </span>
          </div>
          {notifications.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-telekinesis-text-secondary">
              No notifications
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  className={cn(
                    'w-full px-3 py-2.5 text-left transition-colors hover:bg-telekinesis-neutral-800',
                    !n.read && 'bg-telekinesis-neutral-900',
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FFBA28]" />
                    )}
                    <div className={cn('min-w-0 flex-1', n.read && 'pl-3.5')}>
                      <p className="text-xs font-medium leading-snug text-telekinesis-text-primary">
                        {n.title}
                      </p>
                      <p className="mt-0.5 text-[11px] leading-snug text-telekinesis-text-secondary">
                        {n.body}
                      </p>
                      <p className="mt-1 text-[10px] text-telekinesis-text-tertiary">
                        {formatDistanceToNow(new Date(n.created_at), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Guests get literal Log In / Sign Up buttons; signed-in users get the
          Account & Files panel trigger (fixed width matching the sidebar). */}
      {isAnonymous ? (
        <div className="flex h-full shrink-0 items-center gap-2 border-l border-telekinesis-neutral-800 px-3">
          <button
            onClick={() => navigate('/signin')}
            className="flex items-center gap-1.5 rounded px-3 py-1 text-xs text-telekinesis-text-secondary transition-colors hover:bg-telekinesis-neutral-800 hover:text-telekinesis-text-primary"
          >
            <LogIn className="h-3.5 w-3.5" />
            Log In
          </button>
          <button
            onClick={() => navigate('/signup')}
            className="rounded bg-[#107d40] px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-[#0d6634]"
          >
            Sign Up
          </button>
        </div>
      ) : (
        <div
          className={cn(
            'flex h-full w-64 shrink-0 items-center border-l border-telekinesis-neutral-800 text-xs transition-colors',
            isSidebarOpen
              ? 'bg-telekinesis-bg-dark text-telekinesis-text-primary'
              : 'text-telekinesis-text-secondary',
          )}
        >
          {/* Label click toggles the Account & Files panel (unchanged). */}
          <button
            onClick={onToggleRightSidebar}
            className="flex h-full flex-1 items-center gap-2 px-3 transition-colors hover:bg-telekinesis-neutral-900 hover:text-telekinesis-text-primary"
          >
            <UserAvatar className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">Account</span>
            <ChevronDown
              className={cn(
                'h-3 w-3 shrink-0 transition-transform duration-200',
                isSidebarOpen && 'rotate-180',
              )}
            />
          </button>
          {/* Account switcher (#297): other accounts, add account, sign out. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="Switch account"
                className="flex h-full items-center border-l border-telekinesis-neutral-800 px-2 transition-colors hover:bg-telekinesis-neutral-900 hover:text-telekinesis-text-primary"
              >
                <People className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={dropContentClass}>
              <AccountSwitcher itemClassName={dropItemClass} />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
